import { getCompanyInfo } from '@/model/CompanyInfo';

export function getShopTimezoneRaw(): string {
  const companyInfo = getCompanyInfo() || ({} as any);
  return companyInfo.timezone || companyInfo.timezoneFormat || localStorage.getItem('timezone') || 'UTC+8 (Beijing Time)';
}

export function shopTimezoneOffsetMinutes(timezoneRaw?: string): number {
  const timezone = timezoneRaw || getShopTimezoneRaw();
  const m = String(timezone).match(/(?:UTC|GMT)\s*([+-])\s*(\d{1,2})(?::?(\d{2}))?/i);
  if (!m) return 8 * 60;
  const sign = m[1] === '-' ? -1 : 1;
  const hours = Math.min(23, Math.max(0, parseInt(m[2], 10) || 0));
  const minutes = Math.min(59, Math.max(0, parseInt(m[3] || '0', 10) || 0));
  return sign * (hours * 60 + minutes);
}

export function normalizeHms(s: string): string {
  const p = String(s).trim().split(':');
  const hh = Math.min(23, Math.max(0, parseInt(p[0] || '0', 10) || 0));
  const mm = Math.min(59, Math.max(0, parseInt(p[1] || '0', 10) || 0));
  const ss = Math.min(59, Math.max(0, parseInt(p[2] || '0', 10) || 0));
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

export function shopPartsFromTimestamp(ts: number, offsetMin?: number): { date: string; time: string } {
  const off = offsetMin != null ? offsetMin : shopTimezoneOffsetMinutes();
  const d = new Date(ts + off * 60 * 1000);
  const y = d.getUTCFullYear();
  const mo = `${d.getUTCMonth() + 1}`.padStart(2, '0');
  const da = `${d.getUTCDate()}`.padStart(2, '0');
  const hh = `${d.getUTCHours()}`.padStart(2, '0');
  const mm = `${d.getUTCMinutes()}`.padStart(2, '0');
  const ss = `${d.getUTCSeconds()}`.padStart(2, '0');
  return { date: `${y}-${mo}-${da}`, time: `${hh}:${mm}:${ss}` };
}

export function shopDateTimeToTimestamp(date: string, time: string, offsetMin?: number): number {
  const dm = String(date).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const tm = normalizeHms(time).match(/^(\d{2}):(\d{2}):(\d{2})$/);
  if (!dm || !tm) return NaN;
  const off = offsetMin != null ? offsetMin : shopTimezoneOffsetMinutes();
  const utcMs = Date.UTC(
    parseInt(dm[1], 10),
    parseInt(dm[2], 10) - 1,
    parseInt(dm[3], 10),
    parseInt(tm[1], 10),
    parseInt(tm[2], 10),
    parseInt(tm[3], 10)
  );
  return utcMs - off * 60 * 1000;
}

export function formatTodayYmd(offsetMin?: number): string {
  return shopPartsFromTimestamp(Date.now(), offsetMin).date;
}

export function maxEndYmd(offsetMin?: number): string {
  const year = parseInt(formatTodayYmd(offsetMin).slice(0, 4), 10);
  const maxYear = Number.isNaN(year) ? new Date().getFullYear() + 1 : year + 1;
  return `${maxYear}-12-31`;
}

export function tomorrowEndTimestampString(offsetMin?: number): string {
  const today = formatTodayYmd(offsetMin);
  const dm = today.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dm) return String(Date.now());
  const tomorrowUtc = Date.UTC(parseInt(dm[1], 10), parseInt(dm[2], 10) - 1, parseInt(dm[3], 10)) + 24 * 60 * 60 * 1000;
  const d = new Date(tomorrowUtc);
  const ymd = `${d.getUTCFullYear()}-${`${d.getUTCMonth() + 1}`.padStart(2, '0')}-${`${d.getUTCDate()}`.padStart(2, '0')}`;
  return String(shopDateTimeToTimestamp(ymd, '23:59:59', offsetMin));
}

export function parseEndDateParts(raw?: string | null, offsetMin?: number): { date: string; time: string } {
  const s = String(raw || '').trim();
  if (!s) return { date: '', time: '' };
  const asNum = Number(s);
  if (!Number.isNaN(asNum) && asNum > 1e11) {
    return shopPartsFromTimestamp(asNum, offsetMin);
  }
  const dt = s.match(/^(\d{4}-\d{2}-\d{2})[ T](.+)$/);
  if (dt) {
    return { date: dt[1], time: normalizeHms(dt[2].trim()) };
  }
  const dm = s.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (dm) return { date: dm[1], time: '00:00:00' };
  return { date: '', time: '' };
}

export function parseEndTimestamp(raw?: string | null, offsetMin?: number): number {
  const s = String(raw || '').trim();
  if (!s) return NaN;
  const asNum = Number(s);
  if (!Number.isNaN(asNum) && asNum > 1e11) return asNum;
  const p = parseEndDateParts(s, offsetMin);
  if (p.date) return shopDateTimeToTimestamp(p.date, p.time || '00:00:00', offsetMin);
  return NaN;
}
